import type { StudentActionTableRow } from '@/domains/academicRisk';
import { PdfActionDetailGeneratorService } from '@/infra/services/pdfActionDetailGenerator.service';

export interface ExportActionDetailToPdfUseCaseParams {
  action: StudentActionTableRow;
  studentName?: string;
  studentRun?: string;
  riskDescription?: string;
  filename?: string;
  author?: string;
  generatedBy?: string;
  logo?: string;
}

/**
 * UseCase para exportar el detalle de una acción académica a formato PDF
 *
 * Responsabilidades:
 * - Validar que existe información de la acción a exportar
 * - Generar nombre de archivo automático si no se proporciona
 * - Delegar la generación del PDF al servicio especializado
 * - Soportar generación de Blob para previsualización
 */
export class ExportActionDetailToPdfUseCase {
  private readonly pdfGenerator: PdfActionDetailGeneratorService;

  constructor() {
    this.pdfGenerator = new PdfActionDetailGeneratorService();
  }

  /**
   * Ejecuta la exportación del detalle de acción a PDF
   * @param params - Parámetros de exportación (acción, datos del estudiante, metadatos)
   */
  async execute(params: ExportActionDetailToPdfUseCaseParams): Promise<void> {
    const {
      action,
      studentName,
      studentRun,
      riskDescription,
      filename,
      author = 'CFT Maule - Sistema Alerta Temprana',
      generatedBy,
      logo,
    } = params;

    if (!action) {
      throw new Error('No se proporcionó información de la acción');
    }

    const generatedFilename = filename || this.generateFilename(action);

    await this.pdfGenerator.exportActionDetailToPdf({
      action,
      studentName,
      studentRun,
      riskDescription,
      filename: generatedFilename,
      author,
      generatedBy,
      logo,
    });
  }

  /**
   * Genera un Blob del PDF sin descargarlo
   * @param params - Parámetros de exportación
   */
  async generateBlob(
    params: ExportActionDetailToPdfUseCaseParams,
  ): Promise<Blob> {
    const {
      action,
      studentName,
      studentRun,
      riskDescription,
      author = 'CFT Maule - Sistema Alerta Temprana',
      generatedBy,
      logo,
    } = params;

    if (!action) {
      throw new Error('No se proporcionó información de la acción');
    }

    return await this.pdfGenerator.generateActionDetailBlob({
      action,
      studentName,
      studentRun,
      riskDescription,
      author,
      generatedBy,
      logo,
    });
  }

  private generateFilename(action: StudentActionTableRow): string {
    const date = new Date().toISOString().split('T')[0];
    return `accion_${action.cod_accion}_${date}.pdf`;
  }
}
